import { GridStackOptions, GridStackWidget } from 'gridstack';

import {
  BATTERY_COMPONENT_NAME,
  BILGE_PUMP_COMPONENT_NAME,
  BIOMASS_COMPONENT_NAME,
  BIOMASS_DISTRIBUTION_COMPONENT_NAME,
  CAMERA_STREAM_WIDGET,
  CAMERA_WIDGET_COMPONENT_NAME,
  CHART_COMPONENT_NAME,
  EMPTY_WIDGET_COMPONENT_NAME,
  EVENT_COMPONENT_NAME,
  FUEL_USAGE_COMPONENT_NAME,
  GLOBAL_BILGE_PUMP_COMPONENT_NAME,
  GLOBAL_GENERATOR_COMPONENT_NAME,
  LICE_COUNT_COMPONENT_NAME,
  ORBITONE_HEATMAP_WIDGET_COMPONENT_NAME,
  ORBITONE_TABLE_WIDGET_COMPONENT_NAME,
  PEN_COMPONENT_NAME,
  PEN_WIDGET_COMPONENT_NAME,
  SENSOR_WIDGET_COMPONENT_NAME,
  SILO_COMPONENT_NAME,
  TANK_COMPONENT_NAME,
  WELFARE_INDICATOR_WIDGET_COMPONENT_NAME,
  WELFARE_SUMMARY_WIDGET_COMPONENT_NAME,
} from '@/widgetComponentNames.ts';

/**
 * Dimensions of one macro grid cell. Each macro cell is subdivided into
 * `COLUMN_SUBDIVISIONS` fine columns and `ROW_SUBDIVISIONS` fine rows, so the
 * effective column count is `COLUMN_SUBDIVISIONS * ceil(width / COLUMN_BASE_WIDTH)`
 * and each fine row is `ROW_BASE_HEIGHT / ROW_SUBDIVISIONS` px tall.
 */
const COLUMN_BASE_WIDTH = 160;
export const ROW_BASE_HEIGHT = 120;

/**
 * Fine columns per macro cell. The grid is subdivided horizontally so widgets
 * can be split into exact thirds (e.g. three videos side-by-side). The effective
 * column count is always a multiple of this value, guaranteeing divisibility by 3.
 *
 * The fine column count equals `COLUMN_SUBDIVISIONS * (old macro column count)`,
 * which keeps the one-time stored-layout migration a uniform `x → x * 3`,
 * `w → w * 3` transform regardless of screen size.
 */
export const COLUMN_SUBDIVISIONS = 3;

/**
 * Fine rows per macro cell. The grid is subdivided vertically with the same
 * reasoning as `COLUMN_SUBDIVISIONS`, giving finer vertical control (e.g. for
 * 16:9 vs 4:3 video heights). The one-time stored-layout migration is the uniform
 * `y → y * 3`, `h → h * 3` transform that mirrors the horizontal one.
 */
export const ROW_SUBDIVISIONS = 3;

/** Minimum landing-page grid width (fine columns) for camera stream widgets. */
const CAMERA_STREAM_MIN_GRID_WIDTH = 3 * COLUMN_SUBDIVISIONS;

/**
 * Calculates the number of fine columns for a given width.
 * e.g. for 1920px width, it returns 36 columns (3 * ceil(1920 / 160) = 3 * 12).
 */
export const widthPxToGridColumns = (widthPx: number): number =>
  Math.max(1, COLUMN_SUBDIVISIONS * Math.round(widthPx / COLUMN_BASE_WIDTH));

/** Fine column count for the landing-page grid (matches `useScreenSize`). */
export const screenWidthToGridColumns = (widthPx: number): number =>
  Math.max(1, COLUMN_SUBDIVISIONS * Math.ceil(widthPx / COLUMN_BASE_WIDTH));

export const initOptions: GridStackOptions = {
  float: true,
  animate: true,
  margin: `0.125rem`,
  cellHeight: ROW_BASE_HEIGHT / ROW_SUBDIVISIONS,
  column: 12 * COLUMN_SUBDIVISIONS,
  // No responsive `columnOpts`/breakpoints: the column count is pinned to the screen
  // format and the grid is scaled down to fit a narrower viewport (cell widths are CSS
  // percentages of the container), instead of reflowing/wrapping widgets into fewer columns.
  draggable: {
    scroll: false,
  },
  resizable: {
    handles: 'all',
    autoHide: false,
  },
  minRow: 1,
};

export const getWidgetConstraints = (componentName: string) => {
  const S = COLUMN_SUBDIVISIONS;
  const R = ROW_SUBDIVISIONS;
  switch (componentName) {
    case BATTERY_COMPONENT_NAME:
      return { minW: 3 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case CHART_COMPONENT_NAME:
      return { minW: 3 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case EVENT_COMPONENT_NAME:
      return { minW: 6 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case EMPTY_WIDGET_COMPONENT_NAME:
      return { minW: 2 * S, minH: 2 * R } as Partial<GridStackWidget>;
    case FUEL_USAGE_COMPONENT_NAME:
      return { minW: 3 * S, minH: 4 * R } as Partial<GridStackWidget>;
    case GLOBAL_GENERATOR_COMPONENT_NAME:
      return { minW: 2 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case PEN_COMPONENT_NAME:
      return { minW: 4 * S, minH: 4 * R } as Partial<GridStackWidget>;
    case PEN_WIDGET_COMPONENT_NAME:
      return { minW: 4 * S, minH: 2 * R } as Partial<GridStackWidget>;
    case SENSOR_WIDGET_COMPONENT_NAME:
      return { minW: 2 * S, minH: 2 * R } as Partial<GridStackWidget>;
    case TANK_COMPONENT_NAME:
      return { minW: 1 * S, minH: 2 * R } as Partial<GridStackWidget>;
    case GLOBAL_BILGE_PUMP_COMPONENT_NAME:
      return { minW: 3 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case BILGE_PUMP_COMPONENT_NAME:
      return { minW: 2 * S, minH: 2 * R } as Partial<GridStackWidget>;
    case CAMERA_STREAM_WIDGET:
    case CAMERA_WIDGET_COMPONENT_NAME:
      return { minW: CAMERA_STREAM_MIN_GRID_WIDTH, minH: 3 * R } as Partial<GridStackWidget>;
    case SILO_COMPONENT_NAME:
      return { minW: 2 * S, minH: 2 * R } as Partial<GridStackWidget>;
    case BIOMASS_COMPONENT_NAME:
      return { minW: 2 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case LICE_COUNT_COMPONENT_NAME:
      return { minW: 2 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case BIOMASS_DISTRIBUTION_COMPONENT_NAME:
      return { minW: 3 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case WELFARE_SUMMARY_WIDGET_COMPONENT_NAME:
      return { minW: 3 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case WELFARE_INDICATOR_WIDGET_COMPONENT_NAME:
      return { minW: 3 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case ORBITONE_TABLE_WIDGET_COMPONENT_NAME:
      return { minW: 6 * S, minH: 3 * R } as Partial<GridStackWidget>;
    case ORBITONE_HEATMAP_WIDGET_COMPONENT_NAME:
      // Three macro columns (~474px of card) fit the pen labels and four week columns. More weeks
      // scroll sideways until the widget is widened, and rows that do not fit scroll beneath the
      // sticky average row.
      return { minW: 3 * S, minH: 3 * R } as Partial<GridStackWidget>;
    default:
      return { minW: 3 * S, minH: 3 * R } as Partial<GridStackWidget>;
  }
};
